'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Makeup: We Audited 81 Formulas from Fenty, Huda Beauty, rhode and Kylie Cosmetics"
      author="MicroPlastics Research Desk"
      date="July 31, 2026"
      readTime="14 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="Microplastics in makeup brands audited 2026 Fenty Huda Beauty rhode Kylie Cosmetics ingredient lists"
    >
      <BlogLastUpdated iso="2026-07-31" formatted="July 31, 2026" />

      <BlogTLDR>
        We pulled the ingredient lists that <strong>Fenty Beauty, Huda
        Beauty, rhode and Kylie Cosmetics publish on their own
        websites</strong> and counted how many contain a solid synthetic
        polymer particle, the thing EU law actually calls a microplastic.
        The answer: <strong>24 of 81 distinct formulas, or 29%</strong>.
        The most common one is plain <strong>polyethylene</strong>, the
        same polymer as a plastic bag. Named examples we verified on
        30 July 2026: <strong>Huda Beauty&apos;s Easy Bake loose
        powder</strong> lists <strong>Nylon-12</strong> fifth;{' '}
        <strong>Fenty&apos;s Bright Fix blurring powder</strong> lists{' '}
        <strong>Polymethyl Methacrylate</strong> (acrylic) fourth;{' '}
        <strong>Fenty&apos;s Brown&apos;d Pout</strong> lists{' '}
        <strong>Polyethylene Terephthalate</strong>, PET, the water-bottle
        plastic, as glitter; and <strong>rhode&apos;s Peptide Lip
        Shape</strong> lists <strong>polyethylene</strong>. The
        surprise: <strong>rhode&apos;s famous Peptide Lip Treatment and
        Kylie&apos;s Matte Liquid Lipstick contain no solid polymer
        particles at all.</strong> None of this is illegal, and that is
        the actual story, EU law does not touch make-up until{' '}
        <strong>17 October 2035</strong>.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>We counted only <strong>solid, insoluble</strong> polymer particles, the EU legal definition. Liquid silicones (dimethicone, cyclopentasiloxane) and water-soluble PEGs are <em>not</em> microplastics, and most &ldquo;plastic in your makeup&rdquo; content gets this wrong.</>,
          <>24 of 81 distinct formulas (29%) contained at least one. By brand: <strong>Fenty 8 of 14, Huda Beauty 5 of 12, Kylie Cosmetics 4 of 20, rhode 7 of 35.</strong> Sample sizes differ, see the method section, these are not full-catalogue censuses.</>,
          <>Polyethylene was the most common, appearing in 10 formulas, mostly in <strong>lip liners, brow pencils and blush sticks</strong> where it works as a wax hardener.</>,
          <>Powders are the worst category. <strong>Nylon-12 and PMMA</strong> are what make a &ldquo;blurring&rdquo; or &ldquo;baking&rdquo; powder blur and bake, they are microscopic plastic spheres that scatter light.</>,
          <>The EU restriction (Regulation 2023/2055) hits rinse-off products in <strong>2027</strong>, leave-on in <strong>2029</strong>, and lip, nail and make-up products in <strong>2035</strong>. Colour cosmetics got a twelve-year runway.</>,
          <>No mainstream &ldquo;clean beauty&rdquo; standard excludes these ingredients. <Link href="/blog/clean-beauty-microplastics-loophole" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">That loophole is its own article.</Link></>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why we did this the boring way
      </h2>
      <p>
        There is a lot of content claiming that famous beauty brands are
        &ldquo;full of microplastics.&rdquo; Almost none of it names a
        product, quotes an ingredient list, or defines the word. The
        usual move is to flag <strong>dimethicone</strong>, call it
        &ldquo;liquid plastic,&rdquo; and stop there.
      </p>
      <p>
        That is not what a microplastic is. Under{' '}
        <strong>Commission Regulation (EU) 2023/2055</strong>, the law
        that actually restricts these ingredients, a synthetic polymer
        microparticle has to be <strong>solid</strong>, has to be under
        5&nbsp;mm, and has to be <strong>poorly water-soluble</strong>{' '}
        (under 2&nbsp;g/L). Dimethicone is a liquid silicone fluid. It
        fails the first test. PEG compounds dissolve in water. They fail
        the third.
      </p>
      <p>
        Both are legitimate things to have opinions about. Neither is a
        microplastic, and lumping them in makes the real finding
        disappear into the noise. So we went and counted the real thing.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Method, and its limits
      </h2>
      <p>
        On <strong>30 July 2026</strong> we walked each brand&apos;s own
        product sitemap, fetched every product page, and extracted the
        ingredient list <em>as the brand publishes it</em>. No
        third-party ingredient databases, several of the popular ones
        are user-uploaded and years stale.
      </p>
      <p>
        We collapsed shade variants into single formulas, a fourteen-shade
        brow pencil is one formula, not fourteen findings, and then
        counted formulas containing at least one solid synthetic polymer
        particle.
      </p>
      <p><strong>We deliberately did not count:</strong></p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Dimethicone, cyclopentasiloxane, phenyl trimethicone</strong> &mdash; liquid silicone fluids, not solid particles.</li>
        <li><strong>PEG- and PPG- compounds</strong> &mdash; water-soluble, explicitly outside the definition.</li>
        <li><strong>Polyquaternium compounds</strong> &mdash; water-soluble.</li>
        <li><strong>Silica dimethyl silylate</strong> &mdash; silica is a mineral, the silicone is a surface treatment.</li>
        <li><strong>Hydrogenated polyisobutene, polybutene</strong> &mdash; liquid synthetic hydrocarbon polymers. Real petrochemicals, not particles.</li>
      </ul>
      <p>
        <strong>The honest limits.</strong> We only captured formulas
        where the brand publishes a machine-readable ingredient list on
        its own site. That gave us 14 distinct Fenty formulas, 12 Huda,
        20 Kylie and 35 rhode &mdash; a sample, not a census. Fenty in
        particular publishes far more products than it publishes parseable
        ingredient lists for. <strong>The per-brand percentages are
        therefore not directly comparable as a league table</strong>, and
        we would be misleading you if we presented them as one. What the
        numbers support is the pattern: which product <em>categories</em>{' '}
        carry solid polymers, and which specific named products do.
      </p>
      <p className="text-sm text-gray-500 italic">
        Ingredient lists change without notice and vary by market and
        shade. Everything below was true of the ingredient list published
        on the brand&apos;s own site on 30 July 2026. Check the item in
        your hand.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What we found
      </h2>

      <BlogComparisonTable
        caption="Solid synthetic polymer particles in published ingredient lists, checked 30 July 2026"
        columns={[
          { key: 'brand', label: 'Brand' },
          { key: 'formulas', label: 'Distinct formulas captured' },
          { key: 'with', label: 'With a solid polymer particle' },
          { key: 'common', label: 'Most common polymer found' },
        ]}
        rows={[
          { brand: 'Fenty Beauty', formulas: '14', with: '8', common: 'Polyethylene, PMMA, PET' },
          { brand: 'Huda Beauty', formulas: '12', with: '5', common: 'Nylon-12' },
          { brand: 'rhode', formulas: '35', with: '7', common: 'Polyethylene, Acrylates Copolymer' },
          { brand: 'Kylie Cosmetics', formulas: '20', with: '4', common: 'Polyethylene' },
          { brand: 'All four combined', formulas: '81', with: '24 (29%)', common: 'Polyethylene (10 formulas)' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The named products, and what they list
      </h2>

      <BlogComparisonTable
        caption="Specific products whose brand-published ingredient list includes a solid synthetic polymer (30 July 2026)"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'polymer', label: 'Solid polymer listed' },
          { key: 'why', label: 'What it is doing in there' },
        ]}
        rows={[
          { product: <>Huda Beauty Easy Bake Loose Baking & Setting Powder (and the Fragrance Free and Duo versions)</>, polymer: 'Nylon-12, listed 5th', why: <>Microscopic nylon spheres roll under the fingertip and scatter light. This is what “baking” a face actually is.</> },
          { product: <>Fenty Beauty Bright Fix Instant Brightening & Blurring Powder</>, polymer: 'Polymethyl Methacrylate (PMMA / acrylic), listed 4th', why: <>Acrylic microspheres. The soft-focus “blur” is light diffusing off plastic beads.</> },
          { product: <>Fenty Beauty Brown&rsquo;d Pout</>, polymer: 'Polyethylene Terephthalate (PET) + Acrylates Copolymer', why: 'PET is the plastic in a water bottle. Cut into flakes and coated, it is cosmetic glitter. Sits alongside borosilicate pearls.' },
          { product: 'Fenty Beauty Brow MVP Ultra Fine Brow Pencil Styler (14 shades)', polymer: 'Polyethylene', why: 'Hardens the wax core so a fine pencil tip holds its shape.' },
          { product: 'Fenty Beauty Brow MVP Sculpting Wax Pencil', polymer: 'Methyl Methacrylate Crosspolymer + Polyethylene', why: 'Wax structure plus acrylic beads for slip.' },
          { product: 'rhode Peptide Lip Shape (all shades)', polymer: 'Polyethylene', why: 'Same job as the brow pencil: it is what makes a lip liner a solid stick.' },
          { product: 'rhode Barrier Restore Cream, Caffeine Reset, Pineapple Refresh Cleanser', polymer: 'Acrylates Copolymer', why: 'Texture and film-forming. Note the cleanser is rinse-off, which is the category the EU restricts first, in 2027.' },
          { product: 'Kylie Cosmetics King Kylie Matte Lip Kit', polymer: 'Polyethylene', why: 'Wax-structure hardener in the liner component.' },
          { product: 'Kylie Cosmetics High Gloss Liner Duo', polymer: 'Polyethylene', why: 'As above.' },
          { product: 'Kylie Cosmetics Power Plush Longwear Foundation', polymer: 'Dimethicone/Vinyl Dimethicone Crosspolymer', why: <>A silicone <em>elastomer</em>. Unlike liquid dimethicone this is a solid, swollen gel particle, which is why it counts and plain dimethicone does not.</> },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The three products that surprised us
      </h2>
      <p>
        We expected the celebrity lip products to be the worst offenders.
        They were not.
      </p>
      <p>
        <strong>rhode Peptide Lip Treatment</strong>, the most
        photographed beauty product of the last three years, contains{' '}
        <strong>no solid synthetic polymer particles</strong>. Its
        published list runs Hydrogenated Polyisobutene, Diisostearyl
        Malate, shea butter, Polybutene, waxes. Those first and fourth
        ingredients <em>are</em> synthetic petroleum-derived polymers,
        and if your objection to plastic is &ldquo;I don&apos;t want
        petrochemicals on my mouth,&rdquo; that objection stands. But
        they are liquids. They are not microplastics, and we are not
        going to call them microplastics to win a headline.
      </p>
      <p>
        <strong>Kylie Cosmetics Matte Liquid Lipstick</strong> likewise:
        isododecane, mica, dicalcium phosphate, octyldodecanol,
        hydrogenated styrene/isoprene copolymer. That copolymer is an
        elastomeric binder, not a particle. Clean on this specific test.
      </p>
      <p>
        And the reverse surprise: <strong>rhode</strong>, the brand with
        the most minimalist &ldquo;skin barrier&rdquo; positioning of the
        four, put <strong>Acrylates Copolymer in a rinse-off
        cleanser</strong> &mdash; the exact product category the EU
        restricts first.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The pattern that actually matters
      </h2>
      <p>
        Sort the findings by product format instead of by brand and the
        noise resolves into something you can act on:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Loose and pressed powders</strong> (setting, baking, blurring): the highest-risk category, and the one where the polymer is doing the headline job. Nylon-12 and PMMA <em>are</em> the blur.</li>
        <li><strong>Pencils and sticks</strong> (brow, lip liner, cream blush): polyethylene is the default wax hardener. Very common, low concentration.</li>
        <li><strong>Glitter and shimmer</strong> (lip toppers, shadows, highlighters): PET flakes. The most literal plastic in the category.</li>
        <li><strong>Long-wear liquid foundations</strong>: silicone elastomer crosspolymers, the &ldquo;soft-focus&rdquo; texture.</li>
        <li><strong>Liquid lipsticks, balms, oils, serums</strong>: usually clean on this test. Their polymers tend to be liquid.</li>
      </ul>
      <p>
        If you only change one thing, change the powder. It is the
        product applied in the largest quantity, to the largest surface
        area, in the most inhalable form.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Does any of this actually harm you?
      </h2>
      <p>
        Here is where we say what we don&apos;t know, because that is
        the whole point of the site.
      </p>
      <p>
        <strong>There is no published study showing that nylon-12 or
        PMMA in a cosmetic powder causes disease in humans.</strong> Not
        one. Intact skin is a genuinely good barrier, and particles in
        the 1&ndash;50&nbsp;&micro;m range that these powders use are far
        too large to cross it. Anyone telling you your setting powder is
        giving you microplastics in your bloodstream is inventing that.
      </p>
      <p>
        The two arguments that survive scrutiny are narrower:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Inhalation.</strong> Loose powder is aerosolised by design, and it is applied to the face, next to the airway. Particle inhalation is a far better-established route than dermal absorption. This is the exposure route worth caring about, and it is the one with the least cosmetic-specific research.</li>
        <li><strong>Environmental release.</strong> This is why the EU acted, and the reasoning is explicit. Rinse-off products go down the drain. Leave-on products end up on towels and in the wash. ECHA&apos;s estimate for the whole restriction is roughly half a million tonnes of microplastic release prevented over 20 years.</li>
      </ul>
      <p>
        So the fair verdict is: <strong>a real environmental problem, a
        plausible-but-unquantified inhalation question, and no
        demonstrated dermal health risk.</strong> If someone sells you
        certainty in either direction, they are selling something.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why it is all still perfectly legal until 2035
      </h2>
      <p>
        Regulation (EU) 2023/2055 phases in by product type, and
        make-up got the longest runway of anything in the entire
        restriction:
      </p>
      <BlogComparisonTable
        caption="EU microplastics restriction: when each cosmetic category must comply"
        columns={[
          { key: 'cat', label: 'Category' },
          { key: 'date', label: 'Applies from' },
          { key: 'note', label: 'Note' },
        ]}
        rows={[
          { cat: 'Microbeads (rinse-off exfoliants)', date: 'Already banned', note: 'In force since 17 October 2023.' },
          { cat: 'Rinse-off products (cleansers, shampoos, body wash)', date: '17 October 2027', note: <>The first big deadline. rhode&rsquo;s Pineapple Refresh Cleanser sits in this category.</> },
          { cat: 'Leave-on products (creams, serums, most skincare)', date: '17 October 2029', note: 'Two more years.' },
          { cat: 'Lip products, nail products and make-up', date: '17 October 2035', note: 'Twelve years from adoption. Every colour cosmetic in this article is in this bucket.' },
        ]}
      />
      <p>
        Nothing in this article is a claim that any brand broke a rule.
        Every product named is legal to sell in the EU today and will be
        for another nine years. <strong>The gap between what is legal and
        what a shopper assumes is in a $40 powder is the entire
        story.</strong>
      </p>

      <BlogAppCTA
        headline="Check the powder in your bag"
        sub="Nylon-12, PMMA, PET, polyethylene, acrylates copolymer. Scan any makeup product and the app parses the ingredient list, tells you whether it contains a solid polymer particle or just a liquid silicone, and shows you a cleaner same-format swap."
        buttonLabel="Scan a makeup product"
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The brand-by-brand breakdowns
      </h2>
      <p>
        Each of these goes product by product with the full published
        ingredient lists:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><Link href="/blog/does-fenty-beauty-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">Does Fenty Beauty have microplastics?</Link> &mdash; the PET glitter and the PMMA blurring powder.</li>
        <li><Link href="/blog/does-huda-beauty-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">Does Huda Beauty have microplastics?</Link> &mdash; Easy Bake and the nylon question.</li>
        <li><Link href="/blog/does-rhode-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">Does rhode have microplastics?</Link> &mdash; the Peptide Lip Treatment verdict, and where rhode actually slips.</li>
        <li><Link href="/blog/does-kylie-cosmetics-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">Does Kylie Cosmetics have microplastics?</Link> &mdash; ten purity badges, and the one that isn&apos;t there.</li>
        <li><Link href="/blog/clean-beauty-microplastics-loophole" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">Clean beauty&apos;s plastic loophole</Link> &mdash; why &ldquo;Clean at Sephora&rdquo; and a 1,600-ingredient ban list both let polymers straight through.</li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>The full INCI list from the barcode, matched to the specific SKU and shade rather than the product line.</>,
          <>Solid polymer particles (nylon-12, PMMA, polyethylene, PET, acrylates copolymer, silicone elastomer crosspolymers) flagged separately from liquid silicones and water-soluble PEGs, because they are not the same thing.</>,
          <>Product format, powder, pencil, liquid, rinse-off, because format drives both the exposure route and which EU deadline applies.</>,
          <>Which EU restriction date the product falls under (2027, 2029 or 2035).</>,
          <>A cleaner same-format, same-budget alternative when the product you scanned contains a solid polymer.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'Which makeup brand has the most microplastics?',
            answer:
              'Of the four we audited, Fenty Beauty had the highest proportion of captured formulas containing a solid synthetic polymer (8 of 14), followed by Huda Beauty (5 of 12), then rhode (7 of 35) and Kylie Cosmetics (4 of 20). We would caution against reading that as a league table: we could only capture formulas where the brand publishes a parseable ingredient list on its own site, and those sample sizes are very different. The reliable finding is about product categories, powders and pencils carry solid polymers far more often than liquids and balms, regardless of brand.',
          },
          {
            question: 'Is dimethicone a microplastic?',
            answer:
              'No. Under Commission Regulation (EU) 2023/2055 a synthetic polymer microparticle must be a solid particle that is poorly water-soluble. Dimethicone is a liquid silicone fluid, so it falls outside the definition. This matters because a great deal of online content flags dimethicone as the main plastic in cosmetics, which buries the ingredients that actually do meet the definition, nylon-12, PMMA, polyethylene, PET and acrylates copolymer. Silicone elastomer crosspolymers are a separate case: those are solid swollen particles and they do count.',
          },
          {
            question: 'Does Huda Beauty Easy Bake powder contain plastic?',
            answer:
              'The ingredient list Huda Beauty published on its own website on 30 July 2026 for Easy Bake Loose Baking &amp; Setting Powder lists Nylon-12 as the fifth ingredient, after talc, silica and dimethicone. Nylon-12 is a solid synthetic polymer used as a microsphere for slip and light diffusion, and it meets the EU definition of a synthetic polymer microparticle. It is entirely legal, make-up is not restricted in the EU until October 2035.',
          },
          {
            question: 'Does rhode contain microplastics?',
            answer:
              'It depends heavily on the product. rhode Peptide Lip Treatment, the brand&apos;s signature product, contains no solid synthetic polymer particles, though its top ingredients are liquid synthetic polymers (hydrogenated polyisobutene, polybutene). rhode Peptide Lip Shape lip liners list polyethylene, and Barrier Restore Cream, Caffeine Reset and Pineapple Refresh Cleanser list acrylates copolymer. The cleanser is notable because rinse-off products are the first category the EU restricts, in October 2027.',
          },
          {
            question: 'Can microplastics in makeup get into your bloodstream through your skin?',
            answer:
              'There is no evidence that they do, and good reason to think they cannot. The polymer particles used in cosmetic powders are typically 1 to 50 micrometres, far too large to cross intact skin. Anyone claiming your setting powder puts microplastics in your blood is going well beyond the evidence. The exposure routes worth taking seriously are inhalation of loose powder, which is under-researched, and environmental release after washing, which is the reason the EU restriction exists at all.',
          },
          {
            question: 'When does the EU microplastics ban apply to makeup?',
            answer:
              '17 October 2035 for lip products, nail products and make-up. Rinse-off products such as cleansers and shampoos come first, on 17 October 2027, and leave-on skincare follows on 17 October 2029. Rinse-off microbeads were banned back in October 2023. Colour cosmetics received the longest transition period in the entire restriction, twelve years from adoption, because reformulating pigmented products without polymer particles is genuinely hard.',
          },
          {
            question: 'Is "clean beauty" makeup free from microplastics?',
            answer:
              'Not necessarily, and usually not. Clean beauty standards are built around a list of excluded ingredients, typically parabens, sulfates, phthalates, formaldehyde donors and similar. Synthetic polymer microparticles are not on the major published lists. A product can carry a clean certification and still contain nylon-12 or polyethylene, because the certification simply does not ask the question.',
          },
          {
            question: 'What should I actually swap first?',
            answer:
              'Loose setting or baking powder. It is applied in the largest quantity, over the largest area, in the most inhalable form, and the polymer content is the highest of any makeup category, it is the functional ingredient, not a trace additive. Mineral-only alternatives built on silica, rice starch, tapioca starch, kaolin or arrowroot deliver most of the same blur without nylon or PMMA. After that, brow pencils and lip liners, where polyethylene is the standard wax hardener.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'European Commission',
            year: 2023,
            title: 'Commission Regulation (EU) 2023/2055 amending Annex XVII to REACH as regards synthetic polymer microparticles',
            publisher: 'Official Journal of the European Union (EUR-Lex, CELEX 32023R2055)',
            url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32023R2055',
          },
          {
            authors: 'European Chemicals Agency',
            year: 2023,
            title: 'Microplastics: restriction on intentionally added microplastics',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
          {
            authors: 'Fenty Beauty',
            year: 2026,
            title: 'Published product ingredient lists (Bright Fix Instant Brightening & Blurring Powder, Brown’d Pout, Brow MVP Ultra Fine Brow Pencil Styler), retrieved 30 July 2026',
            publisher: 'fentybeauty.com',
            url: 'https://fentybeauty.com/',
          },
          {
            authors: 'Huda Beauty',
            year: 2026,
            title: 'Published product ingredient lists (Easy Bake Loose Baking & Setting Powder and variants), retrieved 30 July 2026',
            publisher: 'hudabeauty.com',
            url: 'https://hudabeauty.com/',
          },
          {
            authors: 'rhode skin',
            year: 2026,
            title: 'Published product ingredient lists (Peptide Lip Treatment, Peptide Lip Shape, Barrier Restore Cream, Pineapple Refresh Cleanser), retrieved 30 July 2026',
            publisher: 'rhodeskin.com',
            url: 'https://www.rhodeskin.com/',
          },
          {
            authors: 'Kylie Cosmetics',
            year: 2026,
            title: 'Published product ingredient lists (Matte Liquid Lipstick, King Kylie Matte Lip Kit, Power Plush Longwear Foundation), retrieved 30 July 2026',
            publisher: 'kyliecosmetics.com',
            url: 'https://kyliecosmetics.com/',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
